import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, map } from 'rxjs';
import { environment } from '@formrails/environments';
import { SessionTimeoutService } from '@formrails/shared/data-access';

export type EnvironmentType = 'ci' | 'sandbox' | 'production';

export interface EnvironmentOption {
  type: EnvironmentType;
  label: string;
  url: string;
}

// Environment URLs - update these with actual hosted URLs
const ENVIRONMENT_OPTIONS: EnvironmentOption[] = [
  { type: 'ci', label: 'CI', url: 'https://formrails-ci.azurewebsites.net' },
  { type: 'sandbox', label: 'Sandbox', url: 'https://formrails-sandbox.azurewebsites.net' },
  // { type: 'production', label: 'Production', url: 'https://formrails.azurewebsites.net' },
];

/**
 * The account control at the right of the header bar: who is signed in, the
 * environment switcher, and signing in or out.
 */
@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './user-menu.component.html',
  styleUrl: './user-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserMenuComponent {
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private sessionTimeout = inject(SessionTimeoutService);

  readonly open = signal(false);
  readonly showTooltip = signal(false);
  readonly showEnvironmentMenu = signal(false);

  // Current environment from the static environment file
  currentEnvironment: EnvironmentType = environment.production ? 'production' :
    (environment.applicationApi.includes('sandbox') ? 'sandbox' : 'ci');

  auth = inject(AuthService);
  isAuthenticated$ = this.auth.isAuthenticated$;
  userFullName$: Observable<string | null> = this.auth.user$.pipe(
    map(user => {
      if (!user) return null;
      const firstName = user.given_name || user['https://pmp-ci.eu/first_name'] || '';
      const lastName = user.family_name || user['https://pmp-ci.eu/last_name'] || '';
      if (firstName && lastName) return `${firstName} ${lastName}`;
      if (user.name) return user.name;
      return user.email || null;
    })
  );

  toggle(event: Event): void {
    event.stopPropagation();
    this.open.update(open => !open);
  }

  handleLogout(): void {
    // Deliberate logout ends the editing session: drop locally stored form
    // and workflow drafts so neither reopens after the next sign-in.
    this.sessionTimeout.clearLocalSessionData();
    this.auth.logout({
      logoutParams: { returnTo: window.location.origin }
    });
    this.open.set(false);
  }

  handleLogin(): void {
    this.auth.loginWithRedirect();
    this.open.set(false);
  }

  toggleEnvironmentMenu(event: Event): void {
    event.stopPropagation();
    this.showEnvironmentMenu.update(v => !v);
  }

  getCurrentEnvironment(): EnvironmentType {
    return this.currentEnvironment;
  }

  getEnvironmentLabel(env: EnvironmentType): string {
    const option = ENVIRONMENT_OPTIONS.find(o => o.type === env);
    return option?.label ?? env.toUpperCase();
  }

  getAllEnvironments(): EnvironmentOption[] {
    return ENVIRONMENT_OPTIONS;
  }

  selectEnvironment(env: EnvironmentOption): void {
    if (env.type !== this.currentEnvironment) {
      window.location.href = env.url;
    } else {
      this.showEnvironmentMenu.set(false);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.open() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.open.set(false);
  }
}
